import React from 'react';

import PropTypes from 'prop-types';

import styled from 'styled-components';

import PromoButtonExtended from 'apps/promo/components/common/promo-button/PromoButtonExtended';
import { PromoDangerouslyHtml } from 'apps/promo/components/common/promo-dangerously-html/PromoDangerouslyHtml';
import { PromoSectionContainer } from 'apps/promo/components/common/PromoSectionContainer';
import PromoIllustration from 'apps/promo/components/common/widgets/Illustration/PromoIllustration';
import promoColors from 'apps/promo/theme/promoColors';
import promoMedia from 'apps/promo/theme/promoMedia';

import GetStartedButton from '../get-started-button/GetStartedButton';

const HeroContainer = styled.div`
    padding-top: ${(props) => (props.$promoDarkPage ? '176px' : props.$headerHidden ? '32px' : '104px')};
    padding-bottom: ${(props) => (props.$promoDarkPage ? '104px' : props.$img && !props.$horizontalLayout ? null : '32px')};
    text-align: center;
    box-sizing: border-box;
    background: ${(props) => promoColors[props.$bgColor]};
    background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[0].url})` : null)};
    background-size: cover;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: ${(props) => (props.$img ? 'space-between' : 'center')};
    min-height: ${(props) => (props.$bgImg ? '600px' : null)};
    overflow: hidden;

    ${promoMedia.retina} {
        background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[1].url})` : null)};
    }

    ${promoMedia.tablet744} {
        background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[2].url})` : null)};
        padding-top: ${(props) => (props.$promoDarkPage ? '180px' : props.$headerHidden ? '48px' : '120px')};
        padding-bottom: ${(props) => (props.$promoDarkPage ? '180px' : props.$img && !props.$horizontalLayout ? null : '48px')};
        min-height: ${(props) => (props.$bgImg ? '540px' : null)};
    }

    ${promoMedia.retina744} {
        background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[3].url})` : null)};
    }

    ${promoMedia.desktop1440} {
        background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[4].url})` : null)};
        text-align: ${(props) => (props.$horizontalLayout ? 'left' : 'center')};
        padding-top: ${(props) => (props.$promoDarkPage ? '256px' : props.$headerHidden ? '64px' : '152px')};
        padding-bottom: ${(props) => (props.$promoDarkPage ? '168px' : props.$img && !props.$horizontalLayout ? null : '64px')};
        min-height: ${(props) => (props.$bgImg ? '718px' : null)};
    }

    ${promoMedia.retina1440} {
        background-image: ${(props) => (props.$bgImg ? `url(${props.$bgImg[5].url})` : null)};
    }
`;

const HeroContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${promoMedia.desktop1440} {
        flex-direction: ${(props) => (props.$horizontalLayout ? 'row' : 'column')};
    }
`;

const HorizontalImg = styled.div`
    ${promoMedia.desktop1440} {
        margin-left: 38px;
        width: 520px;
        height: 520px;
        flex-shrink: 0;
    }
`;

const ExtraContainer = styled.div`
    background: ${promoColors.white};
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: 24px;
    padding: 32px 16px;
    box-sizing: border-box;
    width: 100%;

    ${promoMedia.tablet744} {
        padding: ${(props) => (props.$compactExtraBlock ? '32px' : '48px')};
    }

    ${promoMedia.desktop1440} {
        padding: ${(props) => (props.$compactExtraBlock ? '32px' : '72px')};
        margin-left: 38px;
        width: 520px;
        height: ${(props) => (props.$compactExtraBlock ? 'auto' : '520px')};
        flex-shrink: 0;
    }
`;

const Note = styled.div`
    margin-top: 48px;

    a:link,
    a:visited {
        color: ${promoColors.primary};
    }
`;

const HeroTitle = styled.div`
    margin-bottom: 16px;
    margin-top: ${(props) => (props.$hasLogo ? '24px' : null)};

    ${promoMedia.tablet744} {
        margin-bottom: 24px;
        margin-top: ${(props) => (props.$hasLogo ? '32px' : null)};
    }

    ${promoMedia.desktop1440} {
        margin-bottom: 32px;
        margin-top: ${(props) => (props.$hasLogo ? '44px' : props.$horizontalLayout ? '64px' : null)};
    }
`;

const HeroSubtitle = styled.div`
    max-width: 335px;
    margin: 0 auto 16px;

    ${promoMedia.tablet744} {
        max-width: 504px;
        margin: 0 auto 24px;
    }

    ${promoMedia.desktop1440} {
        max-width: ${(props) => (props.fullwidth ? '100%' : '893px')};
        margin: 0 auto 32px;
    }
`;

const BtnContainer = styled.div`
    margin-bottom: ${(props) => (props.$indent ? '16px' : null)};
    min-height: 40px;

    ${promoMedia.tablet744} {
        margin-bottom: ${(props) => (props.$indent ? '24px' : null)};
    }

    ${promoMedia.desktop1440} {
        margin-bottom: ${(props) => (props.$indent ? '32px' : null)};
        min-height: 48px;
    }
`;
const DemoWrapper = styled.div`
    margin-left: 16px;
    display: inline-block;
`;

export const HeroSection = ({ iconName = 'arrow-right', btnVariant = 'tertiary', isReferralC3P0Page, ...props }) => {
    return (
        <HeroContainer
            $bgColor={props.data?.color?.bgColor}
            $bgImg={props.bgImg}
            $img={props.img}
            $horizontalLayout={props.horizontalLayout}
            $promoDarkPage={props.promoDarkPage}
            $hasLogo={props.hasLogo}
            $headerHidden={props.headerHidden}
        >
            <PromoSectionContainer>
                <HeroContent $horizontalLayout={props.horizontalLayout}>
                    <div>
                        {props.hasLogo && props.img && <PromoIllustration img={props.img} fetchPriorityHigh={props.fetchPriorityHigh} />}
                        {props.title && (
                            <HeroTitle $horizontalLayout={props.horizontalLayout} $hasLogo={props.hasLogo}>
                                <PromoDangerouslyHtml
                                    tag="h1"
                                    variant="h1"
                                    color={props.data?.color?.whiteText ? 'white' : props.data?.color?.titleColor}
                                    gradientColor={props.data?.color?.whiteText ? null : props.data?.color?.titleGradient}
                                    dangerouslyHtml={props.title}
                                />
                            </HeroTitle>
                        )}
                        {props.text && (
                            <HeroSubtitle fullwidth={props.fullwidth}>
                                <PromoDangerouslyHtml
                                    variant="subheader"
                                    color={props.data?.color?.whiteText ? 'white' : 'gray80'}
                                    dangerouslyHtml={props.text}
                                />
                            </HeroSubtitle>
                        )}
                        {props.heroNote && (
                            <HeroSubtitle fullwidth={props.fullwidth}>
                                <PromoDangerouslyHtml
                                    variant="note"
                                    tag="div"
                                    color={props.data?.color?.whiteText ? 'white' : 'gray80'}
                                    dangerouslyHtml={props.heroNote}
                                />
                            </HeroSubtitle>
                        )}
                        {(props.data?.btn || props.data?.btnDemo) && (
                            <BtnContainer $indent={props.img}>
                                {props.data?.btn.isExternalButton ? (
                                    <GetStartedButton buttonText={props.data?.btn?.title} promoDarkPage />
                                ) : (
                                    <>
                                        {props.data?.btn && (
                                            <PromoButtonExtended
                                                btn={props.data?.btn}
                                                btnMobile={props.data?.btn_mobile}
                                                isMobileBtn={props.data?.isMobileBtn}
                                                variant={btnVariant}
                                                isInverted={props.isInvertedBtn}
                                                iconName={props.noIconBtn ? '' : iconName}
                                            />
                                        )}
                                        {props.data?.btnDemo && (
                                            <DemoWrapper>
                                                <PromoButtonExtended
                                                    btn={props.data?.btnDemo}
                                                    btnMobile={props.data?.btn_mobile}
                                                    isMobileBtn={props.data?.isMobileBtn}
                                                    variant={btnVariant}
                                                    isInverted={props.isInvertedBtn}
                                                    iconName={props.noIconBtn ? '' : iconName}
                                                />
                                            </DemoWrapper>
                                        )}
                                    </>
                                )}
                            </BtnContainer>
                        )}
                    </div>
                    {props.horizontalLayout && props.img && !props.hasQrCode && !props.hasLogo && (
                        <HorizontalImg>
                            <PromoIllustration img={props.img} fetchPriorityHigh={props.fetchPriorityHigh} />
                        </HorizontalImg>
                    )}
                    {!isReferralC3P0Page && props.horizontalLayout && props.hasQrCode && (
                        <ExtraContainer $compactExtraBlock={props.compactExtraBlock}>
                            {props.extraComponent}
                            {props.note && (
                                <Note>
                                    <PromoDangerouslyHtml tag="div" dangerouslyHtml={props.note} textAlign={'center'} color={'gray80'} variant={'note'} />
                                </Note>
                            )}
                        </ExtraContainer>
                    )}
                    {isReferralC3P0Page && (
                        <ExtraContainer>
                            <div data-embedded-player="default"></div>
                        </ExtraContainer>
                    )}
                </HeroContent>
            </PromoSectionContainer>
            {!props.horizontalLayout && !props.hasLogo && props.img && (
                <PromoSectionContainer dimension="wide">
                    <PromoIllustration img={props.img} fetchPriorityHigh={props.fetchPriorityHigh} />
                </PromoSectionContainer>
            )}
        </HeroContainer>
    );
};

HeroSection.propTypes = {
    bgImg: PropTypes.array,
    bgColor: PropTypes.oneOf([
        'baseColor',
        'white',
        'primary',
        'primaryB2B',
        'purple80',
        'purple40',
        'purple20',
        'purple5',
        'gray80',
        'gray40',
        'gray20',
        'gray5',
        'aubergine01',
        'aubergine02',
        'aubergine03',
        'aubergine04',
        'eggshell01',
        'eggshell02',
        'eggshell03',
        'passionFruit01',
        'passionFruit02',
        'passionFruit03',
        'oceanBlue01',
        'oceanBlue02',
        'oceanBlue03',
        'blueberryBlue01',
        'blueberryBlue02',
        'blueberryBlue03',
        'jade01',
        'jade02',
        'jade03',
    ]),
    title: PropTypes.string,
    titleGradient: PropTypes.oneOf([
        'aubergine01',
        'aubergine02',
        'aubergine03',
        'passionFruit01',
        'passionFruit02',
        'passionFruit03',
        'oceanBlue01',
        'oceanBlue02',
        'oceanBlue03',
        'blueberryBlue01',
        'blueberryBlue02',
        'blueberryBlue03',
        'jade01',
        'jade02',
        'jade03',
    ]),
    titleColor: PropTypes.oneOf(['baseColor', 'white', 'primary', 'primaryB2B', 'gray80', 'gray40']),
    text: PropTypes.string,
    img: PropTypes.array,
    iconName: PropTypes.string,
    whiteText: PropTypes.bool,
    btnVariant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    horizontalLayout: PropTypes.bool,
    noIconBtn: PropTypes.bool,
    noMargin: PropTypes.bool,
    isInvertedBtn: PropTypes.bool,
    fetchPriorityHigh: PropTypes.bool,
    hasQrCode: PropTypes.bool,
    note: PropTypes.string,
    extraComponent: PropTypes.element,
    compactExtraBlock: PropTypes.bool,
    hasLogo: PropTypes.bool,
    isReferralC3P0Page: PropTypes.bool,
};
